import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Printer, Copy, Check, ShieldCheck, Cpu, Database, Award, BookOpen } from 'lucide-react';
import { USER_INFO, CAREER_PATH } from '../data';

export const DossierModal = ({ isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

  const handleCopyText = () => {
    const textToCopy = `
Dossier of ${USER_INFO.name}
Title: ${USER_INFO.title}
Location: ${USER_INFO.location}

Summary:
${USER_INFO.brief}

Education:
${USER_INFO.education.degree} in ${USER_INFO.education.major}
${USER_INFO.education.school} (${USER_INFO.education.period})

Professional Experience:
${CAREER_PATH.map((exp) => `
- ${exp.role} @ ${exp.company} (${exp.period})
  ${exp.details.join('\n  ')}
`).join('')}

Executive Certifications:
${USER_INFO.certifications.map((c) => `- ${c.name} (${c.issuer})`).join('\n')}

Awards & Distinctions:
${USER_INFO.awards.map((a) => `- ${a.title} (${a.org})`).join('\n')}
    `.trim();

    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const filteredExperience = CAREER_PATH.filter((exp) =>
    exp.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    exp.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
    exp.details.some((d) => d.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto bg-black/80 backdrop-blur-md"
          onClick={onClose}
          data-testid="dossier-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-4xl glass-card rounded-xl border border-slate-800 overflow-hidden shadow-2xl relative flex flex-col my-8"
            style={{ maxHeight: 'calc(100vh - 4rem)' }}
          >
            <div className="bg-slate-950/80 p-4 border-b border-slate-800 flex items-center justify-between sticky top-0 z-10 print:hidden">
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-[#00f0ff] signal-dot" />
                <h2 className="font-display font-semibold text-sm tracking-widest text-[#00f0ff] uppercase">Dossier Matrix: {USER_INFO.name}</h2>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleCopyText} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono font-medium rounded border border-slate-800 bg-slate-900/50 hover:bg-[#00f0ff]/10 hover:text-[#00f0ff] transition-all text-slate-300" data-testid="btn-copy-dossier">
                  {copied ? (<><Check className="w-3.5 h-3.5 text-[#39ff14]" /><span>COPIED</span></>) : (<><Copy className="w-3.5 h-3.5" /><span>COPY MATRIX</span></>)}
                </button>
                <button onClick={() => window.print()} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono font-medium rounded border border-slate-800 bg-slate-900/50 hover:bg-[#39ff14]/10 hover:text-[#39ff14] transition-all text-slate-300" data-testid="btn-print-dossier">
                  <Printer className="w-3.5 h-3.5" /><span>PRINT CODE</span>
                </button>
                <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors" data-testid="btn-close-dossier">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-6 md:p-8 overflow-y-auto space-y-8 bg-slate-950/40">
              <div className="relative">
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Filter dossier nodes (ex: Blockchain, HSBC, API)..." className="w-full px-4 py-2.5 bg-slate-900/80 border border-slate-800 font-mono text-xs rounded text-white focus:outline-none focus:border-[#00f0ff] focus:ring-1 focus:ring-[#00f0ff]/50 transition-all placeholder:text-slate-500" data-testid="input-dossier-search" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 pb-6 border-b border-slate-800/60">
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <h1 className="font-display font-extrabold text-2xl md:text-3xl text-white tracking-tight leading-none">{USER_INFO.name}</h1>
                    <p className="font-mono text-[#00f0ff] text-xs mt-1 md:text-sm tracking-wide">{USER_INFO.title}</p>
                  </div>
                  <p className="text-slate-300 text-xs md:text-sm leading-relaxed font-light">{USER_INFO.brief}</p>
                </div>
                <div className="bg-slate-900/40 p-4 rounded-lg border border-slate-800/40 text-xs font-mono space-y-3">
                  <div className="text-slate-400 uppercase tracking-wider text-[10px] font-bold border-b border-slate-800 pb-1">Telemetry Metadata</div>
                  <div><span className="text-slate-500">Location:</span> <span className="text-slate-300">{USER_INFO.location}</span></div>
                  <div><span className="text-slate-500">Industry:</span> <span className="text-slate-300">Fintech & Digital Ledger</span></div>
                  <div><span className="text-slate-500">Node Hub:</span> <span className="text-[#39ff14] font-semibold uppercase">FAB-NEXTGEN-DXB</span></div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-display font-bold text-xs uppercase tracking-widest text-[#00f0ff] flex items-center gap-2"><Cpu className="w-3.5 h-3.5" /><span>Platform Capability Matrices</span></h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-900/20 rounded border border-slate-800/40">
                    <div className="font-mono text-[#39ff14] text-[10px] uppercase font-semibold">ARCHITECTURES & PAYMENTS</div>
                    <ul className="mt-2 text-xs text-slate-300 space-y-1.5 list-disc list-inside">
                      <li>API Connected Banking Ecosystems</li>
                      <li>Core CIB Trade Finance Integrations</li>
                      <li>High Volume Cash Management & Loans</li>
                      <li>Browser Web Foundations (HSBC standard)</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-slate-900/20 rounded border border-slate-800/40">
                    <div className="font-mono text-[#00f0ff] text-[10px] uppercase font-semibold">DISTRIBUTED LEDGERS (BLOCKCHAIN)</div>
                    <ul className="mt-2 text-xs text-slate-300 space-y-1.5 list-disc list-inside">
                      <li>Layer 1/Layer 2 Consensus Systems</li>
                      <li>Confidential proofs via zk-SNARK Circuits</li>
                      <li>Tokenization & Financial Digital Assets</li>
                      <li>Smart Contract Validation (Solidity/Go)</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-display font-bold text-xs uppercase tracking-widest text-[#9d4edd] flex items-center gap-2"><Database className="w-3.5 h-3.5" /><span>Professional Activity Log (Exp)</span></h3>
                {filteredExperience.length === 0 ? (
                  <p className="text-xs font-mono text-slate-500 italic p-4 bg-slate-900/10 rounded">0 matching node logs located for state "{searchTerm}"</p>
                ) : (
                  <div className="space-y-6">
                    {filteredExperience.map((exp, idx) => (
                      <div key={idx} className="pl-4 border-l-2 border-slate-800 space-y-2 relative">
                        <div className="absolute w-2 h-2 rounded-full bg-slate-800 -left-[5px] top-1.5" />
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                          <h4 className="font-display font-semibold text-sm text-white">{exp.role}</h4>
                          <span className="font-mono text-[10px] text-[#00f0ff] bg-[#00f0ff]/5 px-2 py-0.5 rounded border border-[#00f0ff]/10">{exp.period}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs font-mono text-slate-400">
                          <span className="font-semibold text-slate-300">{exp.company}</span>
                          <span>|</span>
                          <span>{exp.location}</span>
                        </div>
                        <ul className="text-xs text-slate-400 space-y-1 list-disc list-inside">
                          {exp.details.map((detail, dIdx) => <li key={dIdx} className="leading-relaxed">{detail}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                <div className="space-y-4">
                  <h3 className="font-display font-bold text-xs uppercase tracking-widest text-[#ff9e00] flex items-center gap-2"><Award className="w-3.5 h-3.5" /><span>Executive Credentials</span></h3>
                  <div className="bg-slate-900/10 rounded border border-slate-800/40 p-4 space-y-3">
                    {USER_INFO.certifications.map((cert, idx) => (
                      <div key={idx} className="flex items-start gap-2.5 text-xs">
                        <ShieldCheck className="w-4 h-4 text-[#ff9e00] shrink-0 mt-0.5" />
                        <div className="leading-snug">
                          <div className="text-slate-300">{cert.name}</div>
                          <div className="font-mono text-[10px] text-slate-500 mt-0.5">{cert.issuer}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-4" data-testid="dossier-awards-section">
                  <h3 className="font-display font-bold text-xs uppercase tracking-widest text-[#00f0ff] flex items-center gap-2"><Award className="w-3.5 h-3.5" /><span>Honors &amp; Awards</span></h3>
                  <div className="bg-slate-900/10 rounded border border-slate-800/40 p-4 space-y-3">
                    {USER_INFO.awards.map((aw, idx) => (
                      <div key={idx} className="flex items-start gap-2.5 text-xs">
                        <ShieldCheck className="w-4 h-4 text-[#00f0ff] shrink-0 mt-0.5" />
                        <div className="leading-snug">
                          <div className="text-slate-200 font-semibold">{aw.title}</div>
                          <div className="font-mono text-[10px] text-slate-500 mt-0.5">{aw.org}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="font-display font-bold text-xs uppercase tracking-widest text-[#39ff14] flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" /><span>Academic Matrix</span></h3>
                  <div className="bg-slate-900/10 rounded border border-slate-800/40 p-4">
                    <div className="space-y-2 text-xs">
                      <div className="font-semibold text-white">{USER_INFO.education.degree}</div>
                      <div className="font-mono text-[10px] text-[#39ff14] uppercase">{USER_INFO.education.major}</div>
                      <div className="text-slate-400">{USER_INFO.education.school}</div>
                      <div className="font-mono text-[10px] text-slate-500">Timeline: {USER_INFO.education.period}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-slate-950 border-t border-slate-800 font-mono text-[10px] text-slate-500 flex items-center justify-between print:hidden">
              <span>SYNC-GATEWAY OPERATIONAL: V2.4-SECURE</span>
              <span>Dossier index compiled autonomously.</span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
